import type { BgTransform } from './types';

export type PlanRotation = NonNullable<BgTransform['rotation']>;
export interface PlanRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: PlanRotation;
}
interface Point { x: number; y: number }

export function normalizePlanRotation(value: unknown): PlanRotation {
  if (typeof value !== 'number' || !Number.isFinite(value) || value % 90 !== 0) return 0;
  return ((value % 360 + 360) % 360) as PlanRotation;
}

/** Rotate displayed coordinates without rewriting plan-relative data. */
export function rotatePlanPoint(point: Point, rect: PlanRect, inverse = false): Point {
  const rotation = normalizePlanRotation((inverse ? -1 : 1) * (rect.rotation ?? 0));
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  const dx = point.x - cx;
  const dy = point.y - cy;
  switch (rotation) {
    case 90: return { x: cx - dy, y: cy + dx };
    case 180: return { x: cx - dx, y: cy - dy };
    case 270: return { x: cx + dy, y: cy - dx };
    default: return point;
  }
}

export function worldToPlanLocal(world: Point, rect: PlanRect) {
  const point = rotatePlanPoint(world, rect, true);
  return {
    u: rect.width === 0 ? 0 : (point.x - rect.x) / rect.width,
    v: rect.height === 0 ? 0 : (point.y - rect.y) / rect.height,
  };
}

export function planLocalToWorld(item: { u: number; v: number }, rect: PlanRect) {
  return rotatePlanPoint({ x: rect.x + item.u * rect.width, y: rect.y + item.v * rect.height }, rect);
}

/** Axis-aligned bounds used by the viewport scrollbars. */
export function rotatedPlanBounds(rect: PlanRect): PlanRect {
  if ((rect.rotation ?? 0) % 180 === 0) return rect;
  return {
    x: rect.x + (rect.width - rect.height) / 2,
    y: rect.y + (rect.height - rect.width) / 2,
    width: rect.height,
    height: rect.width,
  };
}
